import { createSearchParams } from "react-router-dom";
import { EditContractStep } from "./common/useQueryParams";
import { WIZARD_STEP_KEYS, type PublicEventStep } from "./pages/sourcing-events/buyer/wizard/wizardSteps";
import { BID_WIZARD_STEP_KEYS, type BidStep } from "./pages/sourcing-events/portal/bid/bidWizardSteps";
import { REQUEST_WIZARD_STEP_KEYS, type RequestStep } from "./pages/sourcing-events/portal/request/requestWizardSteps";

const editPublicBidPaths = (
  eventId: string,
  bidId: string,
): Record<BidStep, { pathname: string; search: string }> => {
  const pathName = `/portal/events/${eventId}/tilbud/${bidId}`;
  return Object.fromEntries(
    BID_WIZARD_STEP_KEYS.map((step) => [
      step,
      { pathname: pathName, search: createSearchParams({ step }).toString() },
    ]),
  ) as Record<BidStep, { pathname: string; search: string }>;
};

const editParticipationRequestPaths = (
  eventId: string,
  requestId: string,
): Record<RequestStep, { pathname: string; search: string }> => {
  const pathName = `/portal/events/${eventId}/foresporsel/${requestId}`;
  return Object.fromEntries(
    REQUEST_WIZARD_STEP_KEYS.map((step) => [
      step,
      { pathname: pathName, search: createSearchParams({ step }).toString() },
    ]),
  ) as Record<RequestStep, { pathname: string; search: string }>;
};

const editV2SupplierBidPaths = (
  eventId: string,
  bidId: string,
): Record<BidStep, { pathname: string; search: string }> => {
  const pathName = `/v2/konkurranser/${eventId}/tilbud/${bidId}`;
  return Object.fromEntries(
    BID_WIZARD_STEP_KEYS.map((step) => [
      step,
      { pathname: pathName, search: createSearchParams({ step }).toString() },
    ]),
  ) as Record<BidStep, { pathname: string; search: string }>;
};

const editV2SupplierRequestPaths = (
  eventId: string,
  requestId: string,
): Record<RequestStep, { pathname: string; search: string }> => {
  const pathName = `/v2/konkurranser/${eventId}/foresporsel/${requestId}`;
  return Object.fromEntries(
    REQUEST_WIZARD_STEP_KEYS.map((step) => [
      step,
      { pathname: pathName, search: createSearchParams({ step }).toString() },
    ]),
  ) as Record<RequestStep, { pathname: string; search: string }>;
};

const editPublicEventPaths = (id: string): Record<PublicEventStep, { pathname: string; search: string }> => {
  const pathName = `/sourcing-event/public/${id}/edit`;
  return Object.fromEntries(
    WIZARD_STEP_KEYS.map((step) => [
      step,
      { pathname: pathName, search: createSearchParams({ step }).toString() },
    ]),
  ) as Record<PublicEventStep, { pathname: string; search: string }>;
};

const editEventPaths = (id: string) => {
  const pathName = `/sourcing/event/${id}/edit`;
  return {
    intro: {
      pathname: pathName,
      fullPathName: (): string => {
        return `${pathName}?step=intro`;
      },
      search: createSearchParams({
        step: "intro",
      }).toString(),
    },
    products: {
      pathname: pathName,
      fullPathName: () => `${pathName}?step=products`,
      search: createSearchParams({ step: "products" }).toString(),
    },
    docs: {
      pathname: pathName,
      search: createSearchParams({
        step: "docs",
      }).toString(),
    },
    parties: {
      pathname: pathName,
      search: createSearchParams({
        step: "parties",
      }).toString(),
    },
    review: {
      pathname: pathName,
      search: createSearchParams({
        step: "review",
      }).toString(),
    },
  };
};

const editNegotiationPaths = ({ eventId, negotiationId }: { eventId: string; negotiationId: string }) => {
  const pathName = `/sourcing/event/${eventId}/negotiation/${negotiationId}/edit`;
  return {
    intro: {
      pathname: pathName,
      fullPathName: (): string => {
        return `${pathName}?step=intro`;
      },
      search: createSearchParams({
        step: "intro",
      }).toString(),
    },
    products: {
      pathname: pathName,
      fullPathName: () => `${pathName}?step=products`,
      search: createSearchParams({ step: "products" }).toString(),
    },
    docs: {
      pathname: pathName,
      search: createSearchParams({
        step: "docs",
      }).toString(),
    },
    parties: {
      pathname: pathName,
      search: createSearchParams({
        step: "parties",
      }).toString(),
    },
    review: {
      pathname: pathName,
      search: createSearchParams({
        step: "review",
      }).toString(),
    },
  };
};

const editBidPaths = (id: string) => {
  const pathName = `/sourcing/bid/${id}/edit`;
  return {
    details: {
      pathname: pathName,
      search: createSearchParams({
        step: "details",
      }).toString(),
    },
    products: {
      pathname: pathName,
      search: createSearchParams({
        step: "products",
      }).toString(),
    },
    documents: {
      pathname: pathName,
      search: createSearchParams({
        step: "documents",
      }).toString(),
    },
    review: {
      pathname: pathName,
      search: createSearchParams({
        step: "review",
      }).toString(),
    },
  };
};

const editContractPaths = (id: string, originEvent?: string) => {
  const pathName = `/contracts/${id}/edit`;
  return {
    details: {
      pathname: pathName,
      fullPathName: (step: EditContractStep): string => {
        return `${pathName}?step=${step}`;
      },
      search: createSearchParams({
        step: "details",
        ...(originEvent ? { originEvent } : {}),
      }).toString(),
    },
    documents: {
      pathname: pathName,
      search: createSearchParams({
        step: "docs",
        ...(originEvent ? { originEvent } : {}),
      }).toString(),
    },
    review: {
      pathname: pathName,
      search: createSearchParams({
        step: "review",
        ...(originEvent ? { originEvent } : {}),
      }).toString(),
    },
  };
};

const organizationProfilePaths = (basePath: string) => ({
  pathname: basePath,
  sections: {
    information: "information",
    contracts: "contracts",
    sourcingEvents: "sourcing-events",
    reviewsRisk: "reviews-risk",
  },
  go: (id: string, section = "information") => {
    return `${basePath}/${section}?orgId=${encodeURIComponent(id)}`;
  },
  goToEntry: (id: string, section = "information") => {
    return `${basePath}/${section}?entryId=${encodeURIComponent(id)}`;
  },
  section: (section: string, params: URLSearchParams) => {
    const queryString = params.toString();
    return `${basePath}/${section}${queryString ? "?" + queryString : ""}`;
  },
});

export type OrganizationProfileUrls = ReturnType<typeof organizationProfilePaths>;

export const urls = {
  home: "/",
  emailInvite: "/invite/:emailInviteId",
  projects: {
    viewAll: "/projects",
    view: {
      pathname: "/projects/:projectId",
      go: (projectId: string) => `/projects/${projectId}`,
    },
  },
  events: {
    edit: {
      paths: editEventPaths(":eventId"),
      go: (id: string) => {
        return editEventPaths(id);
      },
      isCurrentPage: (): boolean => {
        const regex = /\/event\/.*\/edit/;
        return regex.test(window.location.href);
      },
    },
    view: {
      pathname: "/sourcing/event/:eventId",
      go: (id: string) => {
        return `/sourcing/event/${id}`;
      },
      question: (eventId: string, questionId: string) => {
        return `/sourcing/event/${eventId}?tab=qa&question=${questionId}`;
      },
    },
    editNegotiation: {
      paths: editNegotiationPaths({ eventId: ":eventId", negotiationId: ":negotiationId" }),
      go: ({ eventId, negotiationId }: { eventId: string; negotiationId: string }) =>
        editNegotiationPaths({ eventId, negotiationId }),
    },
    supplierView: {
      pathname: "/sourcing/event/:eventId/supplier-view",
      go: ({ eventId, negotiationRoundId }: { eventId: string; negotiationRoundId?: string | null }) => {
        return `/sourcing/event/${eventId}/supplier-view${negotiationRoundId ? `?negotiationRoundId=${negotiationRoundId}` : ""}`;
      },
      announcement: (eventId: string, announcementId: string) => {
        return `/sourcing/event/${eventId}/supplier-view?tab=announcements&announcement=${announcementId}`;
      },
      question: (eventId: string, questionId: string) => {
        return `/sourcing/event/${eventId}/supplier-view?tab=qa&question=${questionId}`;
      },
    },
    viewAll: "/sourcing/events",
    viewInvitations: "/sourcing/events/invitations",
  },
  contracts: {
    edit: {
      paths: editContractPaths(":contractId"),
      go: (id: string, originEvent?: string) => {
        return editContractPaths(id, originEvent);
      },
      isCurrentPage: (): boolean => {
        const regex = /\/contracts\/.*\/edit/;

        return regex.test(window.location.href);
      },
    },
    viewSingle: {
      pathname: "/contracts/:contractId",
      go: (id: string) => {
        return `/contracts/${id}`;
      },
      task: (contractId: string, taskId: string) => {
        return `/contracts/${contractId}?tab=tasks&task=${taskId}`;
      },
      documents: (contractId: string) => {
        return `/contracts/${contractId}?tab=documents`;
      },
      reminders: (contractId: string) => {
        return `/contracts/${contractId}?tab=reminders`;
      },
      dialog: (contractId: string) => {
        return `/contracts/${contractId}?tab=dialog`;
      },
    },
    viewAll: "/contracts",
    viewDrafts: "/contracts/drafts",
    viewPublished: "/contracts/published",
    search: "/contracts/search",
    searchV2: "/contracts/search-v2",
    viewTasks: "/contracts/tasks",
    settings: "/contracts/settings",
  },
  publicEvents: {
    edit: {
      paths: editPublicEventPaths(":eventId"),
      go: (id: string) => editPublicEventPaths(id),
    },
  },
  portal: {
    list: "/portal/events",
    view: {
      pathname: "/portal/events/:eventId",
      go: (id: string) => `/portal/events/${id}`,
      question: (eventId: string, questionId: string) => `/portal/events/${eventId}?question=${questionId}`,
      announcement: (eventId: string, announcementId: string) =>
        `/portal/events/${eventId}?announcement=${announcementId}`,
    },
    bid: {
      edit: {
        paths: editPublicBidPaths(":eventId", ":bidId"),
        go: (eventId: string, bidId: string) => editPublicBidPaths(eventId, bidId),
      },
    },
    privateBid: {
      edit: {
        pathname: "/portal/events/:eventId/privat-tilbud/:bidId",
        go: (eventId: string, bidId: string) => `/portal/events/${eventId}/privat-tilbud/${bidId}`,
      },
    },
    request: {
      edit: {
        paths: editParticipationRequestPaths(":eventId", ":requestId"),
        go: (eventId: string, requestId: string) => editParticipationRequestPaths(eventId, requestId),
      },
    },
  },
  admin: "/admin",
  notifications: "/notifications",
  bids: {
    edit: {
      paths: editBidPaths(":bidId"),
      go: (id: string) => {
        return editBidPaths(id);
      },
    },
    viewSingle: {
      pathname: "/sourcing/bids/:bidId",
      go: (id: string) => {
        return `/sourcing/bids/${id}`;
      },
    },
    received: "/sourcing/bids/received",
    viewAll: "/sourcing/my-bids",
  },
  organizations: {
    init: "/organization/init",
    search: "/organizations",
    profile: organizationProfilePaths("/organizations/profile"),
  },
  myProfile: {
    organization: {
      edit: "/organization/edit",
      manageUsers: "/organization/manage-users",
      settings: "/organization/settings",
    },
    personal: {
      view: "/personal-profile",
      edit: "/personal-profile/edit",
    },
  },
  v2: {
    home: "/v2",
    notifications: "/v2/varslinger",
    assistant: "/v2/assistent",
    needs: {
      new: "/v2/meld-inn-behov",
      edit: {
        pathname: "/v2/meld-inn-behov/:needId",
        go: (needId: string) => `/v2/meld-inn-behov/${needId}`,
      },
      view: {
        pathname: "/v2/behov/:needId",
        go: (needId: string) => `/v2/behov/${needId}`,
      },
      mine: "/v2/mine-behov",
      list: "/v2/behovsliste",
    },
    marketSurveys: {
      list: "/v2/markedsundersokelser",
    },
    sourcingEvents: {
      viewAll: "/v2/anskaffelser",
      view: {
        pathname: "/v2/anskaffelser/:eventId",
        go: (eventId: string) => `/v2/anskaffelser/${eventId}`,
        question: (eventId: string, questionId: string) =>
          `/v2/anskaffelser/${eventId}?tab=communication&question=${questionId}`,
        announcement: (eventId: string, announcementId: string) =>
          `/v2/anskaffelser/${eventId}?tab=communication&announcement=${announcementId}`,
      },
      protocol: {
        pathname: "/v2/anskaffelser/:eventId/protokoll",
        url: (eventId: string) => `/v2/anskaffelser/${eventId}/protokoll`,
      },
      bidDetail: {
        pathname: "/v2/anskaffelser/:eventId/tilbud/:bidId",
        go: (eventId: string, bidId: string) => `/v2/anskaffelser/${eventId}/tilbud/${bidId}`,
      },
      participationRequestDetail: {
        pathname: "/v2/anskaffelser/:eventId/foresporsler/:requestId",
        go: (eventId: string, requestId: string) => `/v2/anskaffelser/${eventId}/foresporsler/${requestId}`,
      },
      bseView: {
        pathname: "/v2/anskaffelser/bse/:eventId",
        go: (eventId: string) => `/v2/anskaffelser/bse/${eventId}`,
      },
      bseEdit: {
        pathname: "/v2/anskaffelser/bse/:eventId/rediger",
        go: (eventId: string) => `/v2/anskaffelser/bse/${eventId}/rediger`,
      },
      editNegotiationRound: {
        pathname: "/v2/anskaffelser/:eventId/forhandlinger/:roundId/rediger",
        go: (eventId: string, roundId: string) => `/v2/anskaffelser/${eventId}/forhandlinger/${roundId}/rediger`,
      },
    },
    contracts: {
      viewAll: "/v2/kontrakter",
      active: "/v2/aktive-kontrakter",
      view: {
        pathname: "/v2/kontrakter/:contractId",
        go: (contractId: string) => `/v2/kontrakter/${contractId}`,
      },
      edit: {
        pathname: "/v2/kontrakter/:contractId/rediger",
        go: (contractId: string) => `/v2/kontrakter/${contractId}/rediger`,
      },
    },
    projects: {
      viewAll: "/v2/prosjekter",
      view: {
        pathname: "/v2/prosjekter/:projectId",
        go: (projectId: string) => `/v2/prosjekter/${projectId}`,
      },
    },
    profile: "/v2/profil",
    profileEdit: "/v2/profil/rediger",
    settings: "/v2/innstillinger",
    templates: "/v2/maler",
    users: "/v2/brukere",
    organizations: "/v2/bedrifter",
    organizationProfile: organizationProfilePaths("/v2/bedrifter/profil"),
    organizationProfileEdit: "/v2/bedrifter/profil/rediger",
    supplier: {
      procurements: "/v2/konkurranser",
      procurement: {
        pathname: "/v2/konkurranser/:eventId",
        go: (eventId: string) => `/v2/konkurranser/${eventId}`,
        question: (eventId: string, questionId: string) => `/v2/konkurranser/${eventId}?question=${questionId}`,
        announcement: (eventId: string, announcementId: string) =>
          `/v2/konkurranser/${eventId}?announcement=${announcementId}`,
      },
      bids: "/v2/mine-tilbud",
      bid: {
        edit: {
          paths: editV2SupplierBidPaths(":eventId", ":bidId"),
          go: (eventId: string, bidId: string) => editV2SupplierBidPaths(eventId, bidId),
        },
      },
      requests: "/v2/mine-foresporsler",
      request: {
        edit: {
          paths: editV2SupplierRequestPaths(":eventId", ":requestId"),
          go: (eventId: string, requestId: string) => editV2SupplierRequestPaths(eventId, requestId),
        },
      },
    },
  },
  login: "/login",
  register: "/register",
  emailVerification: "/confirmation",
  completeRecovery: (flowId: string): string => `/login?flow=${flowId}`,
  loginWithReturnUrl: (returnTo: string): string => `/login?return_to=${encodeURIComponent(returnTo)}`,
  registerWithReturnUrl: (returnTo: string): string => `/register?return_to=${encodeURIComponent(returnTo)}`,
  init: "/init",
};
